"use client";

import { useEffect, useRef } from "react";
import { usePathname } from "next/navigation";

import {
  clearLandingProductAnalyticsIdentity,
  hasLandingAnalyticsConsent,
  resolveLandingProductAnalyticsConfig,
  sendLandingPageView,
} from "@/lib/landingProductAnalytics";

const config = resolveLandingProductAnalyticsConfig({
  enabled: process.env.NEXT_PUBLIC_POSTHOG_PRODUCT_ANALYTICS_ENABLED,
  environment: process.env.NEXT_PUBLIC_POSTHOG_PRODUCT_ANALYTICS_ENVIRONMENT,
  host: process.env.NEXT_PUBLIC_POSTHOG_PRODUCT_ANALYTICS_HOST,
  projectToken: process.env.NEXT_PUBLIC_POSTHOG_PRODUCT_ANALYTICS_PROJECT_TOKEN,
  deploySha: process.env.NEXT_PUBLIC_POSTHOG_PRODUCT_ANALYTICS_DEPLOY_SHA,
});

export function LandingProductAnalyticsPageView() {
  const pathname = usePathname();
  const lastSentPathname = useRef<string | null>(null);

  useEffect(() => {
    if (!pathname || pathname === lastSentPathname.current || !config) return;
    let cancelled = false;
    let activeController: AbortController | null = null;
    const send = async () => {
      activeController?.abort();
      activeController = null;
      if (!hasLandingAnalyticsConsent(document.cookie)) {
        clearLandingProductAnalyticsIdentity(window.localStorage);
        lastSentPathname.current = null;
        return;
      }
      const requestController = new AbortController();
      activeController = requestController;
      const sent = await sendLandingPageView({
        config,
        pathname,
        cookieHeader: document.cookie,
        visitorStorage: window.localStorage,
        signal: requestController.signal,
      });
      if (sent && !cancelled && activeController === requestController) {
        lastSentPathname.current = pathname;
      }
    };
    void send();
    window.addEventListener("dronejob:analytics-consent-changed", send);
    return () => {
      cancelled = true;
      activeController?.abort();
      window.removeEventListener("dronejob:analytics-consent-changed", send);
    };
  }, [pathname]);

  return null;
}
